import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useSeo from '@/hooks/useSeo';
import { site } from '@/mocks/manhwa';

const principles = [
  { icon: 'ri-eye-line', title: '亲自试读', desc: '每一部收录的韩漫都经过编辑试读与评估，不收录没看过的作品。' },
  { icon: 'ri-scales-3-line', title: '如实评分', desc: '评分基于真实观感，不注水、不刷分，口碑与热度分别呈现。' },
  { icon: 'ri-leaf-line', title: '持续更新', desc: '书单每周更新，及时补充新作与评分变化，追更不迷路。' },
  { icon: 'ri-heart-line', title: '真心推荐', desc: '从读者角度出发，只推荐真正好看、值得收藏的韩漫佳作。' },
];

const values = [
  { title: '内容原则', desc: '我们坚持内容为王，不堆砌标题，不写空洞推荐语，用真实的试读感受替读者把好第一道关。' },
  { title: '收录标准', desc: '无论是人气大作还是冷门宝藏，只要故事扎实、画工出色、值得一追，就会进入书单。' },
  { title: '更新机制', desc: '每周跟进热门新作与口碑变化，动态调整榜单与书单，让推荐始终保持新鲜与准确。' },
];

export default function AboutPage() {
  useSeo({
    title: '关于我们 - 好看的韩漫推荐书单 | 漫卷书单',
    description: '了解漫卷书单：一份由编辑团队人工试读整理的韩漫推荐书单，坚持亲自试读、如实评分、持续更新，从高分人气到冷门宝藏都如实呈现。',
    keywords: '韩漫推荐,关于我们,韩漫书单,漫卷书单,韩漫推荐书单',
    path: '/about',
    type: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: `关于${site.brand}`,
        url: `${window.location.origin}/about`,
        description: '关于漫卷书单的品牌介绍、定位、理念与内容原则。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: site.brand,
        url: `${window.location.origin}`,
        email: site.email,
        slogan: site.slogan,
      },
    ],
  });

  return (
    <div className="bg-background-50">
      <Navbar />
      <main>
        <div className="relative overflow-hidden bg-foreground-950">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-16 md:py-20">
            <nav aria-label="面包屑" className="flex items-center gap-1.5 text-sm text-background-300">
              <Link to="/" className="hover:text-accent-300 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-background-50">关于我们</span>
            </nav>
            <h1 className="mt-6 font-heading text-3xl font-black text-background-50 md:text-5xl">关于 {site.brand}</h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-background-300">
              一份有人情味的韩漫推荐书单。我们亲自试读每一部作品，只推荐真正好看、值得一追的韩漫佳作。
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950">我们是谁</h2>
              <p className="mt-4 text-base leading-8 text-foreground-700">
                {site.brand} 诞生于 {site.since} 年，是一支热爱漫画的编辑团队运营的韩漫推荐内容站。随着韩漫（条漫）在国内越来越受欢迎，我们注意到很多读者面对海量作品不知道从哪看起，于是决定做一份"替你筛过一遍"的韩漫推荐书单。
              </p>
              <p className="mt-4 text-base leading-8 text-foreground-700">
                从高分人气到冷门宝藏，我们把真正好看的作品按题材整理成书单，帮助新老读者快速找到合口味的韩漫，追更不迷路。
              </p>

              <h3 className="mt-8 font-heading text-xl font-bold text-foreground-950">发展理念</h3>
              <div className="mt-4 space-y-4">
                {values.map((v) => (
                  <div key={v.title} className="rounded-2xl border border-background-200 bg-background-50 p-5">
                    <h4 className="font-heading text-base font-semibold text-primary-700">{v.title}</h4>
                    <p className="mt-2 text-sm leading-6 text-foreground-600">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950">我们的内容原则</h2>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {principles.map((p) => (
                  <div key={p.title} className="rounded-2xl border border-background-200 bg-background-100 p-5">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                      <i className={`${p.icon} text-xl`} />
                    </span>
                    <h4 className="mt-4 font-heading text-base font-semibold text-foreground-950">{p.title}</h4>
                    <p className="mt-2 text-sm leading-6 text-foreground-600">{p.desc}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 rounded-2xl bg-primary-500 p-6 text-background-50">
                <h3 className="font-heading text-lg font-bold text-background-50">用户口碑</h3>
                <div className="mt-4 flex gap-1 text-accent-200">
                  {Array.from({ length: 5 }).map((_, i) => <i key={i} className="ri-star-fill" />)}
                </div>
                <p className="mt-3 text-sm leading-6 text-primary-50">
                  “书单质量很稳，推荐的韩漫几乎不踩雷，冷门宝藏栏目更是挖到了好几部心头好。”
                </p>
                <p className="mt-3 text-sm text-primary-100">—— 来自一位入坑两年的读者</p>
              </div>

              <div className="mt-8 rounded-2xl border border-background-200 bg-background-50 p-6">
                <h3 className="font-heading text-lg font-bold text-foreground-950">联系我们</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-600">
                  想推荐韩漫、反馈建议或洽谈合作？欢迎通过邮件与我们交流，我们会认真阅读每一封来信。
                </p>
                <a href={`mailto:${site.email}`} className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors">
                  <i className="ri-mail-line" /> {site.email}
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <button
        type="button"
        aria-label="返回顶部"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-colors hover:bg-primary-600"
      >
        <i className="ri-arrow-up-line text-lg" />
      </button>
    </div>
  );
}